/**
 * Build-time feature flags for the storefront app.
 *
 * Deliberately a plain constant module, NOT env- or backend-driven: a flag here
 * is a code-level switch a developer flips in a branch/PR, so both the old and
 * the new behavior stay type-checked and reviewable. Because the values are
 * literal constants there is no runtime lookup, no server/client split (this
 * module is safe to import from `"use client"` components) and no cache-key or
 * `turbo.json` `globalEnv` plumbing.
 *
 * NOT the place for:
 *   - per-tenant behavior — that belongs on `StorefrontConfig` (like `layout`),
 *     so the backend can vary it per host;
 *   - per-deploy toggles — those want an env var (see `DATA_SOURCE` in
 *     `packages/api/src/config.ts`, the data-source swap point).
 *
 * Adding a flag: declare its value union next to the flag, add the key to
 * `FEATURE_FLAGS`, and consume it through an exhaustive `Record<Variant, …>` so
 * a new variant fails to compile until every consumer handles it.
 */

/** Card render used by the `/menu` browse surfaces. */
export const ITEM_CARD_VARIANTS = ["default", "compact", "row"] as const

export type ItemCardVariant = (typeof ITEM_CARD_VARIANTS)[number]

export const FEATURE_FLAGS = {
  /**
   * `default` — image-on-top tile (aspect-video photo, stacked meta, full-width
   * action). `compact` — dense list row: small square thumb at the reading
   * start, name/description stacked, price + icon action at the reading end.
   * `row` — photo-less takeaway-menu line: name and description at the
   * reading start, price + round add button at the end, a rule between rows.
   * Usually picked per layout (`@/lib/menu-presentation`) rather than here.
   */
  itemCardVariant: "default" satisfies ItemCardVariant,
} as const
